<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccountId from './components/AccountId.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
import AudioTranscription from './components/AudioTranscription.vue';
import SectionLayout from './components/SectionLayout.vue';
import HelpHint from 'dashboard/components/MesselloHelp/HelpHint.vue';

export default {
  components: {
    BaseSettingsHeader,
    NextButton,
    AccountId,
    BuildInfo,
    AccountDelete,
    AudioTranscription,
    SectionLayout,
    WithLabel,
    NextInput,
    HelpHint,
  },
  setup() {
    const { updateUISettings, uiSettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, uiSettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      features: {},
      aiAutoSuggest: false,
      aiAutoTriage: false,
      kbImportUrl: '',
      kbImporting: false,
      aiCredits: null,
      creditsBuying: '',
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    showAudioTranscriptionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.CAPTAIN
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },
    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
  },
  watch: {
    'currentAccount.id'(id) {
      if (id) {
        this.initializeAccount();
      }
    },
  },
  mounted() {
    // Account already in the store (navigated in): seed immediately.
    if (this.currentAccount.id) {
      this.initializeAccount();
    }
  },
  methods: {
    async initializeAccount() {
      try {
        const {
          name,
          locale,
          id,
          domain,
          support_email,
          features,
          custom_attributes,
        } = this.getAccount(this.accountId);

        const effectiveLocale = this.uiSettings?.locale || locale;
        if (effectiveLocale) {
          this.$root.$i18n.locale = effectiveLocale;
        }
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.features = features;
        this.aiAutoSuggest = !!custom_attributes?.ai_auto_suggest;
        this.aiAutoTriage = !!custom_attributes?.ai_auto_triage;
        this.fetchCredits();
      } catch (error) {
        // Ignore error
      }
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
          ai_auto_suggest: this.aiAutoSuggest,
          ai_auto_triage: this.aiAutoTriage,
        });
        // If user locale is set, update the locale with user locale
        const updatedLocale = this.uiSettings?.locale || this.locale;
        if (updatedLocale) {
          this.$root.$i18n.locale = updatedLocale;
        }
        this.getAccount(this.id).locale = this.locale;
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    async openBillingPortal() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/messello_billing/portal`
        );
        window.location.href = data.url;
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'No active subscription found for this account.'
        );
      }
    },
    async fetchCredits() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/messello_billing/credits`
        );
        this.aiCredits = data;
      } catch (error) {
        this.aiCredits = null;
      }
    },
    async buyCredits(packId) {
      if (this.creditsBuying) return;
      this.creditsBuying = packId;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/messello_billing/buy_credits`,
          { pack: packId }
        );
        window.location.href = data.url;
      } catch (error) {
        useAlert(
          error?.response?.data?.error || 'Could not start checkout.'
        );
        this.creditsBuying = '';
      }
    },
    async importKnowledge() {
      const url = this.kbImportUrl.trim();
      if (!url || this.kbImporting) return;
      this.kbImporting = true;
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/messello_ai_knowledge/import`,
          { url }
        );
        this.kbImportUrl = '';
        useAlert(
          'Import started — draft articles will appear in your Help Center shortly.'
        );
      } catch (error) {
        if (error?.response?.status === 403) {
          useAlert('AI Assist is available on the Growth and Scale plans.');
        } else {
          useAlert(error?.response?.data?.error || 'Could not start the import.');
        }
      } finally {
        this.kbImporting = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="$t('GENERAL_SETTINGS.TITLE')" />
    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <SectionLayout
        :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
        :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
        class="!pt-0"
      >
        <form
          v-if="!uiFlags.isFetchingItem"
          class="grid gap-4"
          @submit.prevent="updateAccount"
        >
          <WithLabel
            name="account-name"
            :has-error="v$.name.$error"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
          >
            <NextInput
              v-model="name"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              @blur="v$.name.$touch"
            />
          </WithLabel>
          <WithLabel
            name="site-language"
            :has-error="v$.locale.$error"
            :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
          >
            <select v-model="locale" class="!mb-0 text-sm">
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyDomainEnabled"
            name="custom-domain"
            :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
          >
            <NextInput
              v-model="domain"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
            <template #help>
              {{
                featureInboundEmailEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
              }}

              {{
                featureCustomReplyDomainEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
              }}
            </template>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyEmailEnabled"
            name="support-email"
            :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
          >
            <NextInput
              v-model="supportEmail"
              type="text"
              class="w-full"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
              "
            />
          </WithLabel>
          <div>
            <NextButton blue :is-loading="isUpdating" type="submit">
              {{ $t('GENERAL_SETTINGS.SUBMIT') }}
            </NextButton>
          </div>
        </form>
      </SectionLayout>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />
    </div>
    <AudioTranscription v-if="showAudioTranscriptionConfig" />
    <AccountId />
    <div class="px-8 py-6 border-t border-n-weak">
      <h4 class="text-base font-medium text-n-slate-12 mb-1">
        AI Assist
        <HelpHint slug="messello-ai-overview" title="Messello AI" />
      </h4>
      <p class="text-sm text-n-slate-11 mb-3">
        When enabled, AI drafts a suggested reply as an internal note on each new
        customer message (Growth and Scale plans). Agents review before sending —
        it is never sent to the customer automatically.
      </p>
      <label
        class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
      >
        <input
          v-model="aiAutoSuggest"
          type="checkbox"
          @change="updateAccount"
        />
        Auto-suggest replies as internal notes
      </label>
      <label
        class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12 mt-2"
      >
        <input v-model="aiAutoTriage" type="checkbox" @change="updateAccount" />
        Auto-tag new conversations with your labels (AI triage)
      </label>
      <div class="mt-5">
        <p class="text-sm text-n-slate-11 mb-2">
          Import your help center or website so the AI can answer from your own
          content. We create draft articles you can review and publish.
          <HelpHint
            slug="import-help-center-for-ai"
            title="Import your help center"
            label="How it works"
          />
        </p>
        <div class="flex gap-2 max-w-xl">
          <input
            v-model="kbImportUrl"
            type="url"
            placeholder="https://yoursite.com/help (a page or sitemap.xml)"
            class="flex-1 border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-12"
          />
          <NextButton blue :is-loading="kbImporting" @click="importKnowledge">
            Import
          </NextButton>
        </div>
      </div>
    </div>
    <div v-if="aiCredits" class="px-8 py-6 border-t border-n-weak">
      <h4 class="text-base font-medium text-n-slate-12 mb-1">
        AI usage &amp; credits
      </h4>
      <p class="text-sm text-n-slate-11 mb-3">
        Your AI balance covers replies, suggestions, triage and knowledge answers.
        The free provider is always tried first, so most usage costs nothing.
      </p>
      <div class="grid grid-cols-3 gap-3 mb-4 max-w-xl">
        <div class="rounded-lg border border-n-weak p-3">
          <div class="text-xs text-n-slate-11">Balance</div>
          <div class="text-lg font-medium text-n-slate-12">
            ${{ aiCredits.balance_usd.toFixed(2) }}
          </div>
        </div>
        <div class="rounded-lg border border-n-weak p-3">
          <div class="text-xs text-n-slate-11">Spent this month</div>
          <div class="text-lg font-medium text-n-slate-12">
            ${{ aiCredits.spent_this_month_usd.toFixed(2) }}
          </div>
        </div>
        <div class="rounded-lg border border-n-weak p-3">
          <div class="text-xs text-n-slate-11">AI deflection (30d)</div>
          <div class="text-lg font-medium text-n-slate-12">
            {{ aiCredits.deflection.rate_pct }}%
          </div>
        </div>
      </div>
      <p class="text-sm text-n-slate-11 mb-2">Top up your balance:</p>
      <div class="flex gap-2 flex-wrap">
        <NextButton
          v-for="pack in aiCredits.packs"
          :key="pack.id"
          faded
          :is-loading="creditsBuying === pack.id"
          @click="buyCredits(pack.id)"
        >
          ${{ pack.price_usd }} → ${{ pack.credits_usd.toFixed(2) }} balance
        </NextButton>
      </div>
    </div>
    <div class="px-8 py-6 border-t border-n-weak">
      <h4 class="text-base font-medium text-n-slate-12 mb-1">
        Billing &amp; subscription
      </h4>
      <p class="text-sm text-n-slate-11 mb-3">
        Manage your plan, payment method and invoices.
      </p>
      <NextButton blue @click="openBillingPortal">
        Manage billing
      </NextButton>
    </div>
    <div v-if="!uiFlags.isFetchingItem && isOnChatwootCloud">
      <AccountDelete />
    </div>
    <BuildInfo />
  </div>
</template>
